/**
 * Implementation inspired by https://github.com/radix-ui/primitives/blob/main/packages/react/id/src/id.tsx.
 *
 * Other implementations:
 *
 * - [reach-ui](https://github.com/reach/reach-ui/blob/develop/packages/auto-id/src/index.tsx)
 * - [radix-ui](https://github.com/radix-ui/primitives/blob/main/packages/react/id/src/id.tsx)
 * - [react-spectrum](https://github.com/adobe/react-spectrum/blob/main/packages/%40react-aria/utils/src/useId.ts)
 * - [fluentui](https://github.com/microsoft/fluentui/blob/master/packages/react-hooks/src/useId.ts)
 * - [Yaska/react-use-id-hook](https://github.com/Yaska/react-use-id-hook/blob/master/src/index.tsx)
 * - [AxisCommunications/react-hooks-shareable](https://github.com/AxisCommunications/react-hooks-shareable/blob/main/src/useId.ts)
 *
 * **Important**: this implementation doesn't work in Concurrent mode + SSR.
 * Concurrent mode is not implemented yet in React, but will be eventually.
 * The problem is that this implementation (as well as **all implementations above**) relies on the order of calls to generate ids -
 * it uses shared counter.
 *
 * The problem can be noticed if you use SSR (next.js for example) with `<StrictMode>` wrapper.
 *
 * For now, we will use this implementation, because there are no better alternatives.
 *
 * This issue reported in other implementations:
 *
 * - https://github.com/radix-ui/primitives/issues/811
 * - https://github.com/adobe/react-spectrum/issues/2231
 * - https://github.com/chakra-ui/chakra-ui/issues/4328
 *
 */

import React, { useContext, createContext, useMemo } from 'react';

type IdContextValue = {
  prefix: number;
  current: number;
};

type IdProviderProps = {
  children: React.ReactNode;
};

const defaultIdContext: IdContextValue = {
  prefix: Math.round(Math.random() * 10000000000),
  current: 0,
};

const IdContext = createContext<IdContextValue>(defaultIdContext);

export const IdProvider = (props: IdProviderProps) => {
  const currentContext = useContext(IdContext);
  const isRootIdProvider = currentContext === defaultIdContext;
  const context: IdContextValue = useMemo(
    () => ({
      prefix: isRootIdProvider ? 0 : ++currentContext.prefix,
      current: 0,
    }),
    [isRootIdProvider, currentContext]
  );

  return <IdContext.Provider value={context} {...props} />;
};

const isBrowser = typeof window !== 'undefined';

/**
 * Accessibility utility component that autogenerates IDs to facilitate WAI-ARIA.
 *
 * Some background:
 *
 *  1. Accessibility APIs such as `aria-describedby` rely heavily on element IDs
 *  2. Requiring developers to put IDs on every element is both cumbersome and error-prone
 *  3. With a component model, we can generate IDs for them!
 *
 *  [online documentation](https://doc.babbel.com/lessonnine/design-system.lib/index.html?path=/docs/components-utils-useid--docs)
 *
 */
export const useId = (providedId?: string) => {
  const context = React.useContext(IdContext);

  if (!isBrowser && context === defaultIdContext) {
    console.warn(
      "You're using the design-system.lib's useId hook. When server rendering, you must wrap your application in an <IdProvider> to ensure consistent ids are generated between the client and server."
    );
  }

  return useMemo(
    () => providedId || `uid-${context.prefix}-${++context.current}`,
    [providedId, context]
  );
};
